// @angular Libraries come first
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  NgModule,
  LOCALE_ID,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';

import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { mapTo, tap, take } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';

// Then libraries maintained outside of @angular
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { ToastrModule } from 'ngx-toastr';
import { MARKED_OPTIONS, MarkdownModule } from 'ngx-markdown';

// MaterialModule may be grouped together internally, but it's just a barrel of external Material Modules
import { MaterialModule } from './components/shared/material.module';

import { CiaoSharedModule } from './components/shared/shared.module';
import { CiaoFormsModule } from './components/shared/forms/forms.module';
import { TripModalV2Module } from './components/views/trip/trip-modal-v2/trip-modal-v2.module';

// End of External Libararies

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import {
  LandingComponent,
  AdminComponent,
  SearchComponent,
  ErrorPageComponent,
  StyleGuideComponent,
  PeoplePageComponent,
} from '~/components/views';
import { ScheduledActionDashboardComponent } from './components/views/dev/scheduled-action-dashboard/scheduled-action-dashboard.component';

import { ErrorInterceptor } from './guards/error.interceptor';
import { WaitForConfigInterceptor } from './guards/wait-for-config.interceptor';
import { ConfigService, AuthService } from './services';
import { ConfigDataService } from './services/config-data.service';

import { PopupGetStartedComponent } from './components/views/landing/popup-get-started/popup-get-started.component';
import { PopupCiao2Component } from './components/views/landing/popup-ciao-2/popup-ciao-2.component';
import { LoadingDelayPopupComponent } from './components/loading-delay-popup/loading-delay-popup.component';

import { PeopleTableComponent } from './components/views/admin/people-page/people-table/people-table.component';
import { PageSelectorComponent } from './components/report-table/page-selector/page-selector.component';
import { FsHeaderComponent } from './components/fs-header/fs-header.component';
import { FsFooterComponent } from './components/fs-footer/fs-footer.component';
import { CopyToClipboardDirective } from './directives/copy-to-clipboard.directive';

import { StyleGuideFormControlsComponent } from './components/views/style-guide/style-guide-form-controls/style-guide-form-controls.component';
import { SendEmailButtonComponent } from './components/views/search/send-email-button/send-email-button.component';
import { OkayModalComponent } from './components/shared/okay-modal/okay-modal.component';
import { UserGroupPageComponent } from './components/views/user-group-page/user-group-page.component';
import { LimitedAccessComponent } from './components/views/limited-access/limited-access.component';
import { UserGroupTableComponent } from './components/views/user-group-page/user-group-table/user-group-table.component';
import { RegistrationComponent } from './components/views/registration/registration.component';
import { UserModalComponent } from './components/views/user-modal/user-modal.component';
import { NgForTrackByIdDirective } from './utilities/ng-for-track-by-id.directive';
import { SortableHeaderComponent } from './components/report-table/sortable-header/sortable-header.component';
import { ComboboxTesterComponent } from './components/views/style-guide/style-guide-form-controls/combobox-tester/combobox-tester.component';
import { HelpComponent } from './components/views/help/help.component';
import { InfoComponent } from './components/views/help/info/info.component';
import { FaqsComponent } from './components/views/help/info/faqs/faqs.component';
import { HelpSearchComponent } from './components/views/help/help-search/help-search.component';
import { MarkedOptionsFactory } from './markdown-options.factory';
import { VersionHistoryComponent } from './components/views/help/info/version-history/version-history.component';
import { RolesPermissionsComponent } from './components/views/help/info/roles-permissions/roles-permissions.component';
import { RegisterUserProfileComponent } from './components/views/help/info/register-user-profile/register-user-profile.component';
import { AnnouncementBarComponent } from './components/shared/announcement-bar/announcement-bar.component';
import { TableAdjustmentsDirective } from './directives/table-adjustments.directive';
import { EasterEggService } from './services/easterEgg.service';
import { TimeoutSignoutComponent } from './components/timeout-signout/timeout-signout.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { QACommandsService } from './services/qacommands.service';
import { DatetimeService } from './services/datetime.service';
import { ScheduledCheckpointModalComponent } from './components/views/trip/trip-page-v3/scheduled-checkpoint-modal/scheduled-checkpoint-modal.component';
import { DevUserSwitcherComponent } from './components/dev-user-switcher/dev-user-switcher.component';

function initializeApplication(
  configService: ConfigService,
  configDataService: ConfigDataService,
  authService: AuthService,
  qaCommandsService: QACommandsService,
  easterEggService: EasterEggService,
  library: FaIconLibrary,
  dateTimeService: DatetimeService
): () => Promise<boolean> {
  return () => {
    configService.loadConfigs();
    authService.loadCurrentUser();
    qaCommandsService.enableEnablingQACommands();
    // Expose timezone diagnostic function for debugging
    (window as any).checkTimezone = () => {
      const profileTz = dateTimeService.timeZone$.value;
      const browserTz = dateTimeService.browserDefaults.timeZone;
      const browserActual = dateTimeService.initialTimezone;
      console.log('=== TIMEZONE DIAGNOSTICS ===');
      console.log('Profile timezone (from user settings):', profileTz);
      console.log('Browser default timezone:', browserTz);
      console.log('Browser actual timezone:', browserActual);
      console.log(
        'Current time in profile TZ:',
        new Date().toLocaleString('en-US', { timeZone: profileTz })
      );
      console.log(
        'Current time in browser TZ:',
        new Date().toLocaleString('en-US', { timeZone: browserActual })
      );
      authService.currentUser$.pipe(take(1)).subscribe((user) => {
        console.log(
          'User profile configSettings:',
          user?.person?.configSettings
        );
        console.log(
          'User clock timezone:',
          user?.person?.configSettings?.clock?.timeZone
        );
      });
    };
    return firstValueFrom(configDataService.loaded$, { defaultValue: undefined }).then((_) => true);
  };
}

@NgModule({
  declarations: [
    AppComponent,
    LandingComponent,
    PopupGetStartedComponent,
    PopupCiao2Component,
    LoadingDelayPopupComponent,
    AdminComponent,
    ScheduledActionDashboardComponent,
    SearchComponent,
    ErrorPageComponent,
    StyleGuideComponent,
    PeoplePageComponent,
    FsHeaderComponent,
    FsFooterComponent,
    CopyToClipboardDirective,
    StyleGuideFormControlsComponent,
    SendEmailButtonComponent,
    UserGroupPageComponent,
    LimitedAccessComponent,
    ComboboxTesterComponent,
    HelpComponent,
    InfoComponent,
    HelpSearchComponent,
    FaqsComponent,
    VersionHistoryComponent,
    RolesPermissionsComponent,
    RegisterUserProfileComponent,
    TableAdjustmentsDirective,
    TimeoutSignoutComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    ToastrModule.forRoot({
      timeOut: 6000,
      positionClass: 'toast-bottom-right',
    }),
    MarkdownModule.forRoot({
      markedOptions: {
        provide: MARKED_OPTIONS,
        useFactory: MarkedOptionsFactory,
      },
    }),
    CiaoFormsModule,
    CiaoSharedModule,
    OkayModalComponent,
    ScheduledCheckpointModalComponent,
    NgForTrackByIdDirective,
    TripModalV2Module,
    UserModalComponent,
    RegistrationComponent,
    AnnouncementBarComponent,
    UserGroupTableComponent,
    PeopleTableComponent,
    PageSelectorComponent,
    SortableHeaderComponent,
    DevUserSwitcherComponent,
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom([BrowserAnimationsModule]),
    provideAppInitializer(() => {
      const initializerFn = initializeApplication(
        inject(ConfigService),
        inject(ConfigDataService),
        inject(AuthService),
        inject(QACommandsService),
        inject(EasterEggService),
        inject(FaIconLibrary),
        inject(DatetimeService)
      );
      return initializerFn();
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: WaitForConfigInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: LOCALE_ID,
      useValue: navigator?.language ?? 'en-US',
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
